import React, {ComponentType} from 'react';
import {ToolName} from '../tools/tool-name';
import {EditorMode} from './editor-mode';
import {MenubarPosition, NavPosition} from '../ui/navbar/nav-position';
import {SampleImage} from '../tools/sample-image';
import {BasicShape, defaultShapes} from './default-shapes';
import {defaultStickers, StickerCategory} from './default-stickers';
import {defaultObjectProps} from './default-object-props';
import {BrushSizes, BrushTypes} from '../tools/draw/draw-defaults';
import {EditorTheme} from './editor-theme';
import type {Frame} from '../tools/frame/frame';
import type {FontFaceConfig} from '@common/ui/font-picker/font-face-config';
import {DEFAULT_THEMES} from './default-themes';
import {DEFAULT_NAV_ITEMS} from './default-nav-items';
import type {Pixpen} from '../pixpen';
import {FileDownloadIcon} from '@common/icons/material/FileDownload';
import {HistoryIcon} from '@common/icons/material/History';
import {ObjectName} from '../objects/object-name';
import packageConfig from '../../package.json';
import {MessageDescriptor} from '@common/i18n/message-descriptor';
import {message} from '@common/i18n/message';
import {
  ButtonColor,
  ButtonVariant,
} from '@common/ui/buttons/get-shared-button-style';
import {IconTree} from '@common/icons/create-svg-icon';

export const PIXPEN_VERSION = packageConfig.version;

export interface NavItem {
  /**
   * unique identifier for this navigation item.
   */
  name: string;

  /**
   * Action to perform when this nav item is clicked. Either name of panel to open or custom function.
   */
  action: Function | ToolName;

  /**
   * Name or url of icon for this navigation item.
   */
  icon: React.ComponentType;
}

export interface ToolbarItemConfig {
  /**
   * Unique identifier for this toolbar item.
   */
  name?: string;

  /**
   * Type for this toolbar item.
   */
  type: 'button' | 'zoomWidget' | 'undoWidget' | 'image';

  /**
   * Url for image when toolbar item type is set to "image".
   */
  src?: string;

  /**
   * Icon that should be shown for this item.
   */
  icon?: ComponentType | IconTree[];

  /**
   * Label that should be shown for this item.
   */
  label?: string | MessageDescriptor;

  /**
   * Style for the item when type is set to "button".
   */
  buttonVariant?: ButtonVariant;

  /**
   * Color for the item when type is set to "button".
   */
  buttonColor?: ButtonColor;

  /**
   * Action that should be performed when user clicks on this item.
   */
  action?: (editor: Pixpen) => void;

  /**
   * On which side of the menubar should this item be shown.
   */
  align?: 'left' | 'center' | 'right';

  /**
   * Menubar items will be sorted based on position. Items with lower position will appear first.
   */
  position?: number;

  /**
   * Whether this toolbar item should only show on mobile.
   */
  mobileOnly?: boolean;

  /**
   * Whether this toolbar item should only show on desktop.
   */
  desktopOnly?: boolean;

  /**
   * List of dropdown menu items that will be shown when this button is clicked.
   */
  menuItems?: {label: string; action: (editor: Pixpen) => void}[];
}

export interface ObjectDefaults {
  /**
   * Default object background color.
   */
  fill?: string;

  /**
   * Whether object can be erased using eraser tool.
   */
  erasable?: boolean;

  /**
   * Default align for text added via pixpen.
   */
  textAlign?:
    | 'initial'
    | 'left'
    | 'center'
    | 'right'
    | 'justify'
    | 'justify-left'
    | 'justify-center'
    | 'justify-right';

  /**
   * Whether text should have an underline.
   */
  underline?: boolean;

  /**
   * Whether text should have a strikethrough line.
   */
  linethrough?: boolean;

  /**
   * Default font style for text added via pixpen.
   */
  fontStyle?: 'normal' | 'italic' | 'oblique';

  /**
   * Default font family for text added via pixpen.
   */
  fontFamily?: string;

  /**
   * Default font size for text added via pixpen.
   */
  fontSize?: number;

  /**
   * Default font weight text added via pixpen.
   */
  fontWeight?:
    | 'bold'
    | 'normal'
    | 100
    | 200
    | 300
    | 400
    | 500
    | 600
    | 700
    | 800
    | 900;

  /**
   * Text border color.
   */
  stroke?: string;

  /**
   * Default object width. Will be 1/4 of canvas size if not specified.
   */
  width?: number;

  /**
   * Default object width. Will be 1/4 of canvas size if not specified.
   */
  height?: number;
}

export interface ObjectControlConfig {
  hideTopLeft?: boolean;
  hideTopRight?: boolean;
  hideBottomRight?: boolean;
  hideBottomLeft?: boolean;
  hideRotatingPoint?: boolean;
  hideFloatingControls?: boolean;
  unlockAspectRatio?: boolean;
  lockMovement?: boolean;
}

export interface PixpenTheme {
  name: string;
  isDark?: boolean;
  colors: Record<string, string>;
}

export interface PixpenConfig {
  /**
   * Selector for the container into which pixpen should be loaded.
   */
  selector: string;

  /**
   * Image or pixpen state that should be loaded into editor with initial load.
   * Will accept url or image/state data.
   */
  image?: string;

  /**
   * Pixpen state to load into the editor.
   */
  state?: string;

  /**
   * Opens new empty canvas at specified size. Alternative to "image" and "state".
   */
  blankCanvasSize?: {width: number; height: number};

  /**
   * Whether images loaded into pixpen will be hosted on another domain from where pixpen is hosted.
   */
  crossOrigin?: boolean;

  /**
   * Adds specified text as watermark on downloaded or exported image.
   */
  watermarkText?: string;

  /**
   * Maximum memory pixpen will use when applying filters.
   * https://support.vebto.com/help-center/articles/10/45/164/filter-texture-size
   */
  textureSize?: number;

  /**
   * From where should pixpen assets be loaded.
   * https://support.vebto.com/help-center/articles/10/45/150/specifying-base-url
   */
  baseUrl?: string;

  /**
   * Currently active language for the editor.
   */
  activeLanguage?: string;

  /**
   * List of available translations.
   */
  languages?: {
    [key: string]: Record<string, string>;
  };

  /**
   * On "save" button click pixpen will automatically send image data to specified url.
   */
  saveUrl?: string;

  /**
   * Called when image is saved via save button, export panel or pixpen API.
   */
  onSave?: Function;

  /**
   * Called when save button is clicked in navbar.
   * If provided, default save behavior will be overridden.
   * Receives image blob and filename.
   */
  onSaveClick?: (blob: Blob, filename: string, format: string) => void | Promise<void>;

  /**
   * Called when pixpen editor is fully loaded.
   */
  onLoad?: Function;

  /**
   * Called when editor is closed (via pixpen API or close button click)
   */
  onClose?: Function;

  /**
   * Called when editor is opened (via pixpen API or custom open button)
   */
  onOpen?: Function;

  /**
   * Called whenever a new file (image or state) is opened via file picker.
   */
  onFileOpen?: Function;

  /**
   * Called when main image is loaded (or changed) in the editor.
   */
  onMainImageLoaded?: Function;

  /**
   * @deprecated UI configuration is now hard-coded. This field exists only for backward compatibility and will be ignored.
   */
  ui?: any;

  /**
   * @deprecated Tools configuration is now hard-coded. This field exists only for backward compatibility and will be ignored.
   */
  tools?: any;

  /**
   * Default styles and behaviour for various objects in pixpen.
   */
  objectDefaults?: {
    /**
     * Styles and behaviour for all objects.
     */
    global?: ObjectDefaults;

    /**
     * Styles and behaviour for new basic shapes (circle, triangle etc.)
     */
    [ObjectName.Shape]?: ObjectDefaults;

    /**
     * Styles and behaviour for new stickers.
     */
    [ObjectName.Sticker]?: ObjectDefaults;

    /**
     * Styles and behaviour for text added to image via pixpen.
     */
    [ObjectName.Text]?: ObjectDefaults;
  };

  /**
   * Visibility and behaviour of object controls.
   */
  objectControls?: {
    /**
     * Object controls and behaviour for all objects.
     */
    global?: ObjectControlConfig;

    /**
     * Object controls and behaviour for new basic shapes (circle, triangle etc.)
     */
    [ObjectName.Shape]?: ObjectControlConfig;

    /**
     * Object controls and behaviour for new stickers.
     */
    [ObjectName.Sticker]?: ObjectControlConfig;

    /**
     * Object controls and behaviour for text added to image via pixpen.
     */
    [ObjectName.Text]?: ObjectControlConfig;
  };

  sentryDsn?: string;
}

// UI Configuration - Hard-coded, not configurable from outside
export const DEFAULT_UI_CONFIG = {
  visible: false,
  mode: EditorMode.OVERLAY,
  forceOverlayModeOnMobile: true,
  activeTheme: EditorTheme.LIGHT,
  themes: DEFAULT_THEMES,
  allowEditorClose: true,
  menubar: {
    items: [
      {
        type: 'undoWidget',
        align: 'left',
      },
      {
        type: 'zoomWidget',
        align: 'right',
        desktopOnly: true,
      },
      /* {
        name: 'history',
        type: 'button',
        icon: HistoryIcon,
        align: 'right',
        desktopOnly: true,
        action: editor => {
          editor.togglePanel('history');
        },
      }, */
      /* {
        name: 'download',
        type: 'button',
        icon: FileDownloadIcon,
        label: message('Done'),
        align: 'right',
        action: editor => {
          if (editor.state.config.ui?.showExportPanel) {
            editor.state.togglePanel('export', true);
          } else {
            editor.tools.export.save();
          }
        },
      }, */
    ],
  },
  nav: {
    position: NavPosition.BOTTOM,
    items: [...DEFAULT_NAV_ITEMS],
  },
  colorPresets: {
    items: [
      'rgb(0,0,0)',
      'rgb(255, 255, 255)',
      'rgb(242, 38, 19)',
      'rgb(249, 105, 14)',
      'rgb(253, 227, 167)',
      'rgb(4, 147, 114)',
      'rgb(30, 139, 195)',
      'rgb(142, 68, 173)',
    ],
  },
} as const;

// Tools Configuration - Hard-coded, not configurable from outside
export const DEFAULT_TOOLS_CONFIG = {
  filter: {
    items: [
      'grayscale',
      'blackWhite',
      'sharpen',
      'invert',
      'vintage',
      'polaroid',
      'kodachrome',
      'technicolor',
      'brownie',
      'sepia',
      'removeColor',
      'brightness',
      'gamma',
      'noise',
      'pixelate',
      'blur',
      'emboss',
      'blendColor',
    ],
  },
  zoom: {
    allowUserZoom: true,
    fitImageToScreen: true,
  },
  crop: {
    allowCustomRatio: true,
    defaultRatio: '1:1',
    presets: [
      {ratio: null, name: 'Custom'},
      {ratio: '1:1', name: 'Square'},
      {ratio: '4:3'},
      {ratio: '16:9'},
      {ratio: '5:3'},
      {ratio: '5:4'},
      {ratio: '6:4'},
      {ratio: '7:5'},
      {ratio: '10:8'},
    ],
  },
  text: {
    defaultText: 'Double click to edit',
    controlsPadding: 6,
    items: [
      {
        family: 'Roboto',
        src: 'fonts/open-sans-v27-latin-ext_latin-regular.woff2',
      },
      {
        family: 'Fuzzy Bubbles',
        src: 'fonts/fuzzy-bubbles-v3-latin-700.woff2',
        descriptors: {weight: '700'},
      },
      {
        family: 'Aleo Bold',
        src: 'fonts/aleo-v4-latin-ext_latin-700.woff2',
        descriptors: {weight: '700'},
      },
      {
        family: 'Amatic SC',
        src: 'fonts/amatic-sc-v16-latin-ext_latin-regular.woff2',
      },
      {
        family: 'Corinthia Bold',
        src: 'fonts/corinthia-v7-latin-ext_latin-700.woff2',
      },
      {
        family: 'Bungee Inline',
        src: 'fonts/bungee-inline-v6-latin-ext_latin-regular.woff2',
      },
      {
        family: 'Robot Slab Bold',
        src: 'fonts/roboto-slab-v16-latin-ext_latin-500.woff2',
      },
      {
        family: 'Carter One',
        src: 'fonts/carter-one-v12-latin-regular.woff2',
      },
      {
        family: 'Cody Star',
        src: 'fonts/codystar-v10-latin-ext_latin-regular.woff2',
      },
      {
        family: 'Fira Sans',
        src: 'fonts/fira-sans-v11-latin-ext_latin_cyrillic-regular.woff2',
      },
      {
        family: 'Krona One',
        src: 'fonts/krona-one-v9-latin-ext_latin-regular.woff2',
      },
      {
        family: 'Kumar One Outline',
        src: 'fonts/kumar-one-outline-v8-latin-ext_latin-regular.woff2',
      },
      {
        family: 'Lobster Two',
        src: 'fonts/lobster-two-v13-latin-regular.woff2',
      },
      {
        family: 'Molle Italic',
        src: 'fonts/molle-v11-latin-ext_latin-italic.woff2',
      },
      {
        family: 'Monoton',
        src: 'fonts/monoton-v10-latin-regular.woff2',
      },
      {
        family: 'Nixie One',
        src: 'fonts/nixie-one-v11-latin-regular.woff2',
      },
      {
        family: 'Permanent Marker',
        src: 'fonts/permanent-marker-v10-latin-regular.woff2',
      },
      {
        family: 'Sancreek',
        src: 'fonts/sancreek-v13-latin-ext_latin-regular.woff2',
      },
      {
        family: 'Stint Ultra Expanded',
        src: 'fonts/stint-ultra-expanded-v10-latin-regular.woff2',
      },
      {
        family: 'VT323',
        src: 'fonts/vt323-v12-latin-ext_latin-regular.woff2',
      },
      {
        family: 'Trash Hand',
        src: 'fonts/TrashHand.ttf',
      },
    ],
  },
  draw: {
    brushSizes: BrushSizes,
    brushTypes: BrushTypes,
  },
  shapes: {
    items: defaultShapes.slice(),
  },
  stickers: {
    items: defaultStickers,
  },
  import: {
    validImgExtensions: ['png', 'jpg', 'jpeg', 'svg', 'gif'],
    fitOverlayToScreen: true,
    openDroppedImageAsBackground: false,
  },
  export: {
    defaultFormat: 'png',
    defaultQuality: 0.8,
    defaultName: 'image',
  },
  frame: {
    items: [
      {
        name: 'basic',
        mode: 'basic',
        size: {
          min: 1,
          max: 35,
          default: 10,
        },
      },
      {
        name: 'pine',
        mode: 'stretch',
        size: {
          min: 1,
          max: 35,
          default: 15,
        },
      },
      {
        name: 'oak',
        mode: 'stretch',
        size: {
          min: 1,
          max: 35,
          default: 15,
        },
      },
      {
        name: 'rainbow',
        mode: 'stretch',
        size: {
          min: 1,
          max: 35,
          default: 15,
        },
      },
      {
        name: 'grunge1',
        display_name: 'grunge #1',
        mode: 'stretch',
        size: {
          min: 1,
          max: 35,
          default: 15,
        },
      },
      {
        name: 'grunge2',
        display_name: 'grunge #2',
        mode: 'stretch',
        size: {
          min: 1,
          max: 35,
          default: 20,
        },
      },
      {
        name: 'ebony',
        mode: 'stretch',
        size: {
          min: 1,
          max: 35,
          default: 15,
        },
      },
      {
        name: 'art1',
        display_name: 'Art #1',
        mode: 'repeat',
        size: {
          min: 10,
          max: 70,
          default: 55,
        },
      },
      {
        name: 'art2',
        display_name: 'Art #2',
        mode: 'repeat',
        size: {
          min: 10,
          max: 70,
          default: 55,
        },
      },
    ],
  },
} as const;

export const DEFAULT_CONFIG: PixpenConfig = {
  selector: 'pixpen-editor',
  textureSize: 4096,
  activeLanguage: 'en',
  objectDefaults: {
    global: {
      ...defaultObjectProps,
    },
    sticker: {
      fill: undefined,
    },
    text: {
      textAlign: 'initial',
      underline: false,
      linethrough: false,
      fontStyle: 'normal',
      fontFamily: 'Times New Roman',
      fontWeight: 'normal',
      stroke: undefined,
      fontSize: 40,
    },
  },
};
